import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaClock, FaSpinner, FaEdit, FaBan, FaTrash, FaCheckCircle, FaTimesCircle, FaHourglassHalf } from 'react-icons/fa';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Modal from '../../../components/shared/Modal';
import ModalNotification from '../../../components/shared/ModalNotification';
import formatPhone from '../../../components/shared/formatPhone';
import {
  listScheduledMessages,
  updateScheduledMessage,
  cancelScheduledMessage,
  deleteScheduledMessage,
} from '../../../services/scheduledMessageService';

// ── Styled ──
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: ${p => p.theme.spacing(3)};
  h1 { color: ${p => p.theme.colors.primary}; margin: 0; font-size: 1.4rem; }
  svg { color: ${p => p.theme.colors.primary}; font-size: 1.3rem; }
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: ${p => p.theme.spacing(2.5)};
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px solid ${p => p.$active ? p.theme.colors.primary : p.theme.colors.inputGray + '44'};
  background: ${p => p.$active ? p.theme.colors.primary + '22' : 'transparent'};
  color: ${p => p.$active ? p.theme.colors.primary : p.theme.colors.textSecondary};
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${p => p.theme.colors.primary}88; }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.inputGray}22;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
  th, td { padding: 10px 14px; text-align: left; border-bottom: 1px solid ${p => p.theme.colors.inputGray}22; vertical-align: top; }
  th {
    background: ${p => p.theme.colors.background};
    color: ${p => p.theme.colors.textSecondary};
    font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 700;
  background: ${p => p.color}22;
  color: ${p => p.color};
  white-space: nowrap;
`;

const BodyPreview = styled.div`
  max-width: 320px;
  color: ${p => p.theme.colors.textPrimary};
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  margin-right: 6px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.inputGray}44;
  background: transparent;
  color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.textSecondary};
  font-size: 0.78rem;
  cursor: pointer;
  &:hover { border-color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary}; color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary}; }
`;

const EditField = styled.div`
  margin-bottom: 16px;
  label { display: block; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: ${p => p.theme.colors.textSecondary}; margin-bottom: 6px; }
  textarea {
    width: 100%; min-height: 100px; padding: 10px 12px; resize: vertical; font-family: inherit;
    background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
    border: 1.5px solid ${p => p.theme.colors.inputGray}44; border-radius: ${p => p.theme.borderRadius};
  }
  input {
    width: 100%; padding: 8px 12px;
    background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
    border: 1.5px solid ${p => p.theme.colors.inputGray}44; border-radius: ${p => p.theme.borderRadius};
  }
`;

const STATUS_META = {
  pending:  { label: 'Pending',   color: '#4FC3F7', icon: <FaHourglassHalf size={10} /> },
  sending:  { label: 'Sending',   color: '#FFB74D', icon: <FaSpinner size={10} /> },
  sent:     { label: 'Sent',      color: '#66BB6A', icon: <FaCheckCircle size={10} /> },
  failed:   { label: 'Failed',    color: '#ef5350', icon: <FaTimesCircle size={10} /> },
  canceled: { label: 'Canceled',  color: '#999',    icon: <FaBan size={10} /> },
};

const FILTERS = [
  { key: 'upcoming', label: 'Upcoming', params: { upcoming: 'true' } },
  { key: 'sent', label: 'Sent', params: { status: 'sent' } },
  { key: 'failed', label: 'Failed', params: { status: 'failed' } },
  { key: 'canceled', label: 'Canceled', params: { status: 'canceled' } },
  { key: 'all', label: 'All', params: {} },
];

const ScheduledMessagesPage = () => {
  const [filterKey, setFilterKey] = useState('upcoming');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });

  // Edit modal
  const [editing, setEditing] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editTime, setEditTime] = useState(null);
  const [saving, setSaving] = useState(false);

  // Cancel modal
  const [cancelTarget, setCancelTarget] = useState(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const filter = FILTERS.find(f => f.key === filterKey) || FILTERS[0];
      const res = await listScheduledMessages(filter.params);
      setMessages(res.messages || []);
    } catch (err) {
      setFeedback({ show: true, message: 'Failed to load scheduled messages', success: false });
    } finally {
      setLoading(false);
    }
  }, [filterKey]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const openEdit = (msg) => {
    setEditing(msg);
    setEditBody(msg.body);
    setEditTime(new Date(msg.scheduled_time));
  };

  const saveEdit = async () => {
    if (!editBody.trim()) {
      setFeedback({ show: true, message: 'Message cannot be empty', success: false });
      return;
    }
    if (!editTime || editTime.getTime() <= Date.now()) {
      setFeedback({ show: true, message: 'Pick a date/time in the future', success: false });
      return;
    }
    setSaving(true);
    try {
      await updateScheduledMessage(editing.id, {
        body: editBody.trim(),
        scheduled_time: editTime.toISOString(),
      });
      setEditing(null);
      setFeedback({ show: true, message: 'Scheduled message updated', success: true });
      fetchMessages();
    } catch (err) {
      setFeedback({ show: true, message: err.response?.data?.message || 'Failed to update', success: false });
    } finally {
      setSaving(false);
    }
  };

  const confirmCancel = async () => {
    try {
      await cancelScheduledMessage(cancelTarget.id);
      setCancelTarget(null);
      setFeedback({ show: true, message: 'Scheduled message cancelled', success: true });
      fetchMessages();
    } catch (err) {
      setCancelTarget(null);
      setFeedback({ show: true, message: err.response?.data?.message || 'Failed to cancel', success: false });
    }
  };

  const handleDelete = async (msg) => {
    try {
      await deleteScheduledMessage(msg.id);
      setFeedback({ show: true, message: 'Scheduled message deleted', success: true });
      fetchMessages();
    } catch (err) {
      setFeedback({ show: true, message: err.response?.data?.message || 'Failed to delete', success: false });
    }
  };

  const formatTime = (t) => new Date(t).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  return (
    <DashboardLayout>
      <PageHeader>
        <FaClock />
        <h1>Scheduled Messages</h1>
      </PageHeader>

      <Tabs>
        {FILTERS.map(f => (
          <Tab key={f.key} $active={filterKey === f.key} onClick={() => setFilterKey(f.key)}>
            {f.label}
          </Tab>
        ))}
      </Tabs>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px', color: '#F69620' }}>
          <FaSpinner style={{ fontSize: 32, animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Phone</th>
                <th>Scheduled</th>
                <th>Status</th>
                <th>Message</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {messages.map(msg => {
                const meta = STATUS_META[msg.status] || STATUS_META.pending;
                const name = msg.recipient_name || msg.lead?.name || '—';
                return (
                  <tr key={msg.id}>
                    <td style={{ fontWeight: 500, color: '#fff' }}>{name}</td>
                    <td>{formatPhone(msg.to_number || '')}</td>
                    <td>{formatTime(msg.scheduled_time)}</td>
                    <td>
                      <Badge color={meta.color}>{meta.icon} {meta.label}</Badge>
                      {msg.status === 'failed' && msg.error_message && (
                        <div style={{ color: '#ef5350', fontSize: '0.72rem', marginTop: 4 }}>{msg.error_message}</div>
                      )}
                    </td>
                    <td><BodyPreview>{msg.body}</BodyPreview></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {msg.status === 'pending' ? (
                        <>
                          <ActionBtn onClick={() => openEdit(msg)}><FaEdit /> Edit</ActionBtn>
                          <ActionBtn $danger onClick={() => setCancelTarget(msg)}><FaBan /> Cancel</ActionBtn>
                        </>
                      ) : (
                        <ActionBtn $danger onClick={() => handleDelete(msg)}><FaTrash /> Delete</ActionBtn>
                      )}
                    </td>
                  </tr>
                );
              })}
              {messages.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    No scheduled messages
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      {/* Edit modal */}
      <Modal
        title="Edit Scheduled Message"
        isOpen={!!editing}
        onCancel={() => setEditing(null)}
        onConfirm={saveEdit}
      >
        <EditField>
          <label>Message</label>
          <textarea
            value={editBody}
            maxLength={250}
            onChange={(e) => setEditBody(e.target.value)}
          />
          <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#999' }}>{editBody.length}/250</div>
        </EditField>
        <EditField>
          <label>Deliver on</label>
          <DatePicker
            selected={editTime}
            onChange={(date) => setEditTime(date)}
            showTimeSelect
            timeIntervals={15}
            minDate={new Date()}
            dateFormat="MM/dd/yyyy h:mm aa"
          />
        </EditField>
        {saving && <div style={{ color: '#F69620', fontSize: '0.84rem' }}>Saving...</div>}
      </Modal>

      {/* Cancel confirm */}
      <Modal
        title="Cancel Scheduled Message"
        isOpen={!!cancelTarget}
        onCancel={() => setCancelTarget(null)}
        onConfirm={confirmCancel}
      >
        <p style={{ color: '#ccc', lineHeight: 1.6 }}>
          Cancel the message to <strong style={{ color: '#fff' }}>{cancelTarget?.recipient_name || cancelTarget?.lead?.name || cancelTarget?.to_number}</strong> scheduled for{' '}
          <strong style={{ color: '#fff' }}>{cancelTarget && formatTime(cancelTarget.scheduled_time)}</strong>? It will not be sent.
        </p>
      </Modal>

      <ModalNotification
        show={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </DashboardLayout>
  );
};

export default ScheduledMessagesPage;
